/**
 * Axios instance that automatically attaches a Microsoft Entra Bearer token
 * to every request.
 */
import axios from "axios";
import { msalInstance, loginRequest } from "../auth/msalConfig";
import type {
  Workspace,
  UALEvent,
  PrivilegeChange,
  PublicArtifact,
  PermissionSnapshot,
  DeploymentOperation,
  RefreshRecord,
  ComplianceSummary,
  ReconciliationSummary,
  ReconciliationGap,
  AppSnapshot,
  AppUser,
  ReportSubscription,
  ImportRecord,
  DataflowRecord,
  PimActivation,
  PimActivationSummary,
  SecurityAlert,
  SecurityAlertSummary,
  EDiscoverySearchRequest,
  EDiscoverySearchStatus,
  EDiscoveryResult,
  RefreshScheduleConfig,
  SlaBreachRecord,
  DataflowRefreshRecord,
  RefreshCalendar,
  SystemInfoResponse,
  AdhocRequest,
} from "../types";

const baseURL =
  process.env.REACT_APP_API_BASE_URL ?? window.location.origin;

const apiClient = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor — acquires a token silently before each call
apiClient.interceptors.request.use(async (config) => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // Not authenticated — let the request fail naturally so AuthGuard can redirect
    return config;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    config.headers = config.headers ?? {};
    config.headers["Authorization"] = `Bearer ${result.accessToken}`;
  } catch (err) {
    // Silent acquisition failed (expired refresh token, revoked consent,
    // changed conditional-access policy). Start an interactive login, then
    // abort this request.
    //
    // acquireTokenRedirect only *begins* a navigation, so awaiting it does not
    // stop execution. Previously the interceptor fell through and returned the
    // config unchanged, sending the request with no Authorization header — the
    // user saw an unexplained 401 error page for the half-second before the
    // redirect took effect, and any handler that treated 401 as "no data"
    // rendered an empty result instead.
    msalInstance.acquireTokenRedirect(loginRequest).catch(() => {
      /* navigation failure is surfaced by MSAL's own event handlers */
    });
    throw new axios.Cancel("Redirecting to sign in");
  }

  return config;
});

export async function setWorkspaceMonitoring(
  workspaceId: string,
  monitored: boolean
): Promise<Workspace> {
  const res = await apiClient.patch<Workspace>(
    `/api/workspaces/${workspaceId}/monitoring`,
    { monitored }
  );
  return res.data;
}

export async function fetchUALEvents(params: Record<string, string> = {}): Promise<UALEvent[]> {
  const res = await apiClient.get<UALEvent[]>("/api/ual-events", { params });
  return res.data;
}

export async function fetchPrivilegeChanges(params: Record<string, string> = {}): Promise<PrivilegeChange[]> {
  const res = await apiClient.get<PrivilegeChange[]>("/api/privilege-changes", { params });
  return res.data;
}

export async function fetchPublicArtifacts(params: Record<string, string> = {}): Promise<PublicArtifact[]> {
  const res = await apiClient.get<PublicArtifact[]>("/api/public-artifacts", { params });
  return res.data;
}

export async function fetchPermissions(artifactId: string): Promise<PermissionSnapshot> {
  const res = await apiClient.get<PermissionSnapshot>(`/api/permissions/${artifactId}`);
  return res.data;
}

export async function fetchPermissionChanges(params: Record<string, string> = {}): Promise<PermissionSnapshot[]> {
  const res = await apiClient.get<PermissionSnapshot[]>("/api/permissions/changes", { params });
  return res.data;
}

export async function fetchDeployments(params: Record<string, string> = {}): Promise<DeploymentOperation[]> {
  const res = await apiClient.get<DeploymentOperation[]>("/api/deployments", { params });
  return res.data;
}

export async function fetchRefreshHealth(params: Record<string, string> = {}): Promise<RefreshRecord[]> {
  const res = await apiClient.get<RefreshRecord[]>("/api/refresh-health", { params });
  return res.data;
}

export async function fetchComplianceSummary(): Promise<ComplianceSummary> {
  const res = await apiClient.get<ComplianceSummary>("/api/compliance/summary");
  return res.data;
}

export async function fetchReconciliationSummary(
  params: { date_from?: string; date_to?: string } = {}
): Promise<ReconciliationSummary> {
  const res = await apiClient.get<ReconciliationSummary>("/api/reconciliation/summary", { params });
  return res.data;
}

export async function fetchReconciliationGaps(
  params: { date_from?: string; date_to?: string } = {}
): Promise<ReconciliationGap[]> {
  const res = await apiClient.get<ReconciliationGap[]>("/api/reconciliation/gaps", { params });
  return res.data;
}

// Apps

export async function fetchApps(params: Record<string, string> = {}): Promise<AppSnapshot[]> {
  const res = await apiClient.get<AppSnapshot[]>("/api/apps", { params });
  return res.data;
}

export async function fetchApp(appId: string): Promise<AppSnapshot> {
  const res = await apiClient.get<AppSnapshot>(`/api/apps/${appId}`);
  return res.data;
}

export async function fetchAppUsers(appId: string): Promise<AppUser[]> {
  const res = await apiClient.get<AppUser[]>(`/api/apps/${appId}/users`);
  return res.data;
}

// Subscriptions

export async function fetchSubscriptions(params: Record<string, string> = {}): Promise<ReportSubscription[]> {
  const res = await apiClient.get<ReportSubscription[]>("/api/subscriptions", { params });
  return res.data;
}

export async function fetchReportSubscriptions(reportId: string): Promise<ReportSubscription[]> {
  const res = await apiClient.get<ReportSubscription[]>(`/api/reports/${reportId}/subscriptions`);
  return res.data;
}

// Imports

export async function fetchImports(params: Record<string, string> = {}): Promise<ImportRecord[]> {
  const res = await apiClient.get<ImportRecord[]>("/api/imports", { params });
  return res.data;
}

// Dataflows

export async function fetchDataflows(params: Record<string, string> = {}): Promise<DataflowRecord[]> {
  const res = await apiClient.get<DataflowRecord[]>("/api/dataflows", { params });
  return res.data;
}

export async function fetchDataflow(dataflowId: string): Promise<DataflowRecord> {
  const res = await apiClient.get<DataflowRecord>(`/api/dataflows/${dataflowId}`);
  return res.data;
}

// PIM Activations

export async function fetchPimActivations(params: Record<string, string> = {}): Promise<PimActivation[]> {
  const res = await apiClient.get<PimActivation[]>("/api/pim-activations", { params });
  return res.data;
}

export async function fetchPimSummary(): Promise<PimActivationSummary> {
  const res = await apiClient.get<PimActivationSummary>("/api/pim-activations/summary");
  return res.data;
}

// Security Alerts

export async function fetchSecurityAlerts(params: Record<string, string> = {}): Promise<SecurityAlert[]> {
  const res = await apiClient.get<SecurityAlert[]>("/api/security-alerts", { params });
  return res.data;
}

export async function fetchSecurityAlertsSummary(): Promise<SecurityAlertSummary> {
  const res = await apiClient.get<SecurityAlertSummary>("/api/security-alerts/summary");
  return res.data;
}

// E-Discovery

export async function submitEdiscoverySearch(body: EDiscoverySearchRequest): Promise<EDiscoverySearchStatus> {
  const res = await apiClient.post<EDiscoverySearchStatus>("/api/ediscovery/search", body);
  return res.data;
}

export async function pollEdiscoveryStatus(queryId: string): Promise<EDiscoverySearchStatus> {
  const res = await apiClient.get<EDiscoverySearchStatus>(`/api/ediscovery/search/${queryId}/status`);
  return res.data;
}

export async function fetchEdiscoveryResults(
  queryId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<EDiscoveryResult> {
  const res = await apiClient.get<EDiscoveryResult>(`/api/ediscovery/search/${queryId}/results`, { params });
  return res.data;
}

// Refresh SLA monitoring

export async function fetchRefreshSchedules(params: Record<string, string | boolean> = {}): Promise<RefreshScheduleConfig[]> {
  const res = await apiClient.get<RefreshScheduleConfig[]>("/api/refresh-health/schedules", { params });
  return res.data;
}

export async function fetchSlaBreaches(params: Record<string, string | boolean> = {}): Promise<SlaBreachRecord[]> {
  const res = await apiClient.get<SlaBreachRecord[]>("/api/refresh-health/sla-breaches", { params });
  return res.data;
}

/**
 * Acknowledge an SLA breach.
 *
 * The acknowledging identity is deliberately not sent: the API records it from
 * the validated token. This previously passed a caller-supplied
 * `acknowledgedBy`, which the API trusted — and the value the UI actually sent
 * was the literal string "unknown", so every acknowledgement in the SOX
 * evidence trail was attributed to nobody.
 */
export async function acknowledgeSlaBreach(
  datasetId: string,
  breachDetectedAt: string,
  note?: string
): Promise<SlaBreachRecord> {
  const res = await apiClient.post<SlaBreachRecord>(
    `/api/refresh-health/sla-breaches/${encodeURIComponent(datasetId)}/${encodeURIComponent(breachDetectedAt)}/acknowledge`,
    note ? { note } : {}
  );
  return res.data;
}

export async function fetchRefreshCalendar(
  datasetId: string,
  year: number,
  month: number
): Promise<RefreshCalendar> {
  const res = await apiClient.get<RefreshCalendar>("/api/refresh-health/calendar", {
    params: { dataset_id: datasetId, year, month },
  });
  return res.data;
}

export async function fetchDataflowRefreshes(params: Record<string, string | number> = {}): Promise<DataflowRefreshRecord[]> {
  const res = await apiClient.get<DataflowRefreshRecord[]>("/api/refresh-health/dataflow-refreshes", { params });
  return res.data;
}

// System info

export async function fetchSystemInfo(): Promise<SystemInfoResponse> {
  const res = await apiClient.get<SystemInfoResponse>("/api/system/info");
  return res.data;
}

export async function triggerCollector(collectType: string): Promise<{ triggered: boolean; collect_type: string; status_code: number }> {
  const res = await apiClient.post<{ triggered: boolean; collect_type: string; status_code: number }>(
    "/api/system/trigger-collector",
    { collect_type: collectType }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Ad-hoc collection requests

export async function requestCollection(
  workspaceId: string,
  artifactType: string,
  artifactId: string,
  artifactName?: string
): Promise<AdhocRequest> {
  const res = await apiClient.post<AdhocRequest>("/api/adhoc-requests", {
    workspaceId,
    artifactType,
    artifactId,
    artifactName,
  });
  return res.data;
}

/** The request for one artifact, or null if it was never requested. */
export async function fetchCollectionRequest(
  workspaceId: string,
  artifactType: string,
  artifactId: string
): Promise<AdhocRequest | null> {
  try {
    const res = await apiClient.get<AdhocRequest>(
      `/api/adhoc-requests/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
        artifactType
      )}/${encodeURIComponent(artifactId)}`
    );
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

export interface SystemLogEvent {
  timestamp: string;
  level: string;
  logger: string;
  collector: string;
  message: string;
  error: string;
  function: string;
}

export interface SystemLogsResponse {
  events: SystemLogEvent[];
  queryStatus: string;
  queryId: string;
  logGroups: string[];
  totalReturned: number;
}

export async function fetchSystemLogs(params: {
  minutes?: number;
  level?: string;
  collector?: string;
  search?: string;
  limit?: number;
}): Promise<SystemLogsResponse> {
  const res = await apiClient.get<SystemLogsResponse>("/api/system/logs", { params });
  return res.data;
}

// --- Gateways ---

export interface GatewayMember {
  id: string;
  name: string;
  machine: string;
  status: string;
  state: string;
  version: string;
  versionStatus: string;
}

export interface GatewaySummary {
  gatewayId: string;
  name: string;
  type: string;
  isPersonal: boolean;
  isVirtualNetwork: boolean;
  clusterUrl: string;
  version: string;
  /** Worst support status across the cluster's member nodes. */
  versionStatus: string;
  unsupportedMembers: number;
  memberCount: number;
  members: GatewayMember[];
  contacts: string[];
  department: string;
  lastSyncedAt: string;
  connectionCount: number;
  /** "api" — the gateway endpoints answered, so connections and user
   *  permissions are available. "derived" — inferred from the datasource
   *  bindings we already collect, so dependency counts are accurate but
   *  connection and permission detail needs gateway API access. */
  source: "api" | "derived";
  datasourceCount: number;
  datasetCount: number;
  /** Regions of the workspaces whose data this gateway moves. A gateway has no
   *  region of its own; more than one entry means it crosses a boundary. */
  regionsServed: string[];
  workspacesServed: number;
  lastSeenAt: string;
}

export interface GatewayConnection {
  datasourceId: string;
  gatewayId: string;
  datasourceName: string;
  datasourceType: string;
  connectionDetails: Record<string, string>;
  credentialType: string;
  lastSyncedAt: string;
  userCount: number;
  reports: Array<{ reportId: string; reportName: string; workspaceName: string; workspaceId: string; webUrl: string }>;
}

export interface GatewayConnectionUser {
  emailOrId: string;
  displayName: string;
  datasourceAccessRight: string;
  principalType: string;
  lastSyncedAt: string;
}

export async function fetchGateways(): Promise<GatewaySummary[]> {
  const res = await apiClient.get<GatewaySummary[]>("/api/gateways");
  return res.data;
}

// ── Capacities and geographic regions ──────────────────────────────────────

export interface Capacity {
  capacityId: string;
  displayName: string;
  sku: string;
  region: string;
  state: string;
  admins: string[];
  workspaceCount: number;
  monitoredWorkspaceCount: number;
  lastSyncedAt?: string;
}

export interface RegionSummary {
  region: string;
  capacityCount: number;
  skus: string[];
  workspaceCount: number;
  personalWorkspaceCount: number;
  monitoredWorkspaceCount: number;
  reportCount: number;
  gatewayCount: number;
  /** The region holds real workspaces but none are monitored, so its reports,
   *  permissions and refreshes are never collected. */
  hasBlindSpot: boolean;
}

export interface RegionCoverage {
  homeRegion: string;
  regions: RegionSummary[];
  totalWorkspaces: number;
  totalMonitoredWorkspaces: number;
  unresolvedCapacityWorkspaces: number;
}

export async function fetchCapacities(): Promise<Capacity[]> {
  const res = await apiClient.get<Capacity[]>("/api/capacities");
  return res.data;
}

export async function fetchRegionCoverage(): Promise<RegionCoverage> {
  const res = await apiClient.get<RegionCoverage>("/api/regions/coverage");
  return res.data;
}

// ── Workspace 360 ──────────────────────────────────────────────────────────

export interface WorkspacePerson {
  email: string;
  displayName: string;
  principalType: string;
  roles: string[];
  /** How many artifacts in this workspace the person can reach. */
  artifactCount: number;
}

export interface WorkspaceOverview {
  workspace: Workspace;
  reportCount: number;
  datasetCount: number;
  dataflowCount: number;
  appCount: number;
  /** null when nothing has ever counted them: dashboards have no collector,
   *  so a number only appears when a live read supplied one. */
  dashboardCount: number | null;
  people: WorkspacePerson[];
  /** Flat inventory of every artifact in the workspace, so each count above
   *  leads somewhere instead of being a dead end. */
  artifacts: ArtifactSummary[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recentChanges: Record<string, any>[];
  changeCount30d: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  publicArtifacts: Record<string, any>[];
  gateways: {
    gatewayId: string;
    name: string;
    source: string;
    datasourceCount: number;
    /** Every region this gateway serves, not only this workspace's. */
    regionsServed: string[];
  }[];
  /** null when no refresh history exists — "unknown", not "zero failures". */
  failedRefreshes7d: number | null;
  lastRefreshAt: string;
  /** "collected" = scheduled collection; "live" = nothing was collected, so
   *  this was read from the admin API for this request; "mixed" = collected
   *  data with live values filling the gaps; "unavailable" = neither, and
   *  detailNote says why. */
  detailSource: "collected" | "live" | "mixed" | "unavailable";
  detailNote: string;
  /** False when no permission snapshot exists, so an empty people list means
   *  "not known" rather than "nobody has access". */
  permissionsCollected: boolean;
  refreshDataAvailable: boolean;
  /** False when this workspace's artifacts were never collected. Gateway
   *  dependencies are derived from collected datasource bindings, so an empty
   *  gateway list only means "cloud-only" when this is true. */
  artifactsCollected: boolean;
}

/** All five artifact kinds share one shape so they share one table. */
export type ArtifactType = "report" | "dataset" | "dataflow" | "dashboard" | "app";

export interface ArtifactSummary {
  artifactId: string;
  artifactType: ArtifactType;
  name: string;
  /** "collected" carries a known collection time and is audit evidence;
   *  "live" was read from the admin API for this request. */
  source: "collected" | "live";
  webUrl: string;
  datasetId: string;
  modifiedDateTime: string;
  /** null when no permission snapshot exists, so access is unknown rather
   *  than zero. */
  userCount: number | null;
  lastRefreshAt: string;
  refreshStatus: string;
  publiclyShared: boolean;
}

export interface ArtifactProperty {
  label: string;
  value: string;
}

export interface ArtifactPerson {
  email: string;
  displayName: string;
  role: string;
  principalType: string;
}

export interface ArtifactDetail {
  artifactId: string;
  artifactType: ArtifactType;
  name: string;
  workspaceId: string;
  workspaceName: string;
  source: "collected" | "live";
  webUrl: string;
  properties: ArtifactProperty[];
  people: ArtifactPerson[];
  permissionsCollected: boolean;
  permissionSnapshotAt: string;
  accessChanges: {
    added?: ArtifactPerson[];
    removed?: ArtifactPerson[];
    roleChanged?: {
      email: string;
      displayName?: string;
      previousRole?: string;
      newRole?: string;
    }[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datasources: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshHistory: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshSchedule: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changes: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  publicExposure: Record<string, any>[];
  relatedReports: { reportId: string; name: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscriptions: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  usage: Record<string, any> | null;
  /** Which sections can hold data for this kind of artifact, so the UI hides
   *  a tab rather than showing one that is empty by design. */
  sections: string[];
  note: string;
}

export interface ArtifactChangeFilters {
  dateFrom?: string;
  dateTo?: string;
  actorEmail?: string;
}

export async function fetchArtifactDetail(
  workspaceId: string,
  artifactType: string,
  artifactId: string,
  showSystem = false,
  filters: ArtifactChangeFilters = {}
): Promise<ArtifactDetail> {
  const params: Record<string, string> = {};
  if (showSystem) params.show_system = "true";
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.actorEmail) params.actor_email = filters.actorEmail;
  const res = await apiClient.get<ArtifactDetail>(
    `/api/workspaces/${workspaceId}/artifacts/${artifactType}/${encodeURIComponent(
      artifactId
    )}`,
    { params }
  );
  return res.data;
}

export async function fetchWorkspaceOverview(
  workspaceId: string,
  showSystem = false
): Promise<WorkspaceOverview> {
  const res = await apiClient.get<WorkspaceOverview>(
    `/api/workspaces/${workspaceId}/overview`,
    { params: showSystem ? { show_system: "true" } : {} }
  );
  return res.data;
}

export interface WorkspaceChangesPage {
  // Same shape as WorkspaceOverview.recentChanges, which is the first page of
  // this same list and feeds the same table.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: Record<string, any>[];
  /**
   * The whole filtered window, or null when this read stopped early.
   *
   * Counting it on every page turn would mean re-reading every row in the
   * window to show a hundred of them. When it is null the count to show is the
   * overview's `changeCount30d` — same window, same filters.
   */
  total: number | null;
  offset: number;
  limit: number;
  windowDays: number;
}

export async function fetchWorkspaceChanges(
  workspaceId: string,
  { offset = 0, limit = 100, days = 30, showSystem = false } = {}
): Promise<WorkspaceChangesPage> {
  const res = await apiClient.get<WorkspaceChangesPage>(
    `/api/workspaces/${workspaceId}/changes`,
    {
      params: {
        offset,
        limit,
        days,
        ...(showSystem ? { show_system: "true" } : {}),
      },
    }
  );
  return res.data;
}

export async function fetchGatewayConnections(gatewayId: string): Promise<GatewayConnection[]> {
  const res = await apiClient.get<GatewayConnection[]>(`/api/gateways/${gatewayId}/connections`);
  return res.data;
}

export async function fetchConnectionUsers(gatewayId: string, datasourceId: string): Promise<GatewayConnectionUser[]> {
  const res = await apiClient.get<GatewayConnectionUser[]>(`/api/gateways/${gatewayId}/connections/${datasourceId}/users`);
  return res.data;
}

// --- User preferences (theme, timezone, date/time formats) ---

export interface UserPreferences {
  theme: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

export interface UserPreferencesResponse extends UserPreferences {
  updatedAt: string | null;
  /** Server time at the moment of the response, used to detect client clock skew. */
  serverTime: string;
}

export async function fetchPreferences(): Promise<UserPreferencesResponse> {
  const res = await apiClient.get<UserPreferencesResponse>("/api/preferences");
  return res.data;
}

export async function savePreferences(prefs: UserPreferences): Promise<UserPreferencesResponse> {
  const res = await apiClient.put<UserPreferencesResponse>("/api/preferences", prefs);
  return res.data;
}

export async function fetchTimezones(): Promise<{ timezones: string[]; default: string; serverTime: string }> {
  const res = await apiClient.get<{ timezones: string[]; default: string; serverTime: string }>(
    "/api/preferences/timezones"
  );
  return res.data;
}

/**
 * Tenant-wide settings, as opposed to the per-user preferences above. Admin
 * only — both calls answer 403 for any other role, so the Settings page must
 * not reach for them unless the caller holds Admin.
 */
export interface ActorVisibilitySetting {
  /** True hides this application's own service identities from non-admins. */
  enabled: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  /** False means nobody has changed it and the default is what is in force. */
  everSet: boolean;
  /** How many identities the filter matches. Zero means it can hide nothing. */
  identityCount: number;
}

export async function fetchActorVisibility(): Promise<ActorVisibilitySetting> {
  const res = await apiClient.get<ActorVisibilitySetting>("/api/settings/actor-visibility");
  return res.data;
}

export async function saveActorVisibility(enabled: boolean): Promise<ActorVisibilitySetting> {
  const res = await apiClient.put<ActorVisibilitySetting>("/api/settings/actor-visibility", {
    enabled,
  });
  return res.data;
}

export default apiClient;
